<template>
  <div class="studio-panel">
    <h2>Partner API Keys</h2>
    <p class="studio-panel-subtitle">Manage API keys for editorial integrations</p>

    <div v-if="loading" class="studio-loading">
      <span class="studio-loading-dot"></span>
      <span class="studio-loading-text">Loading API keys...</span>
    </div>

    <div v-if="!loading">
      <div v-if="keys.length > 0" class="keys-list">
        <div v-for="key in keys" :key="key.id" class="studio-card key-card">
          <div class="key-header">
            <span class="key-name">{{ key.name || 'API Key' }}</span>
            <span class="key-status" :class="key.revoked ? 'revoked' : 'active'">
              {{ key.revoked ? 'Revoked' : 'Active' }}
            </span>
          </div>
          <div class="key-prefix">{{ key.prefix }}...</div>
          <div class="key-meta">
            Created {{ formatDate(key.created) }}
            <span v-if="key.expiry_date"> · Expires {{ formatDate(key.expiry_date) }}</span>
          </div>
        </div>
      </div>

      <div v-else class="studio-stub">
        <span class="studio-stub-icon">✦</span>
        <p>No API keys yet</p>
        <p style="font-size: 12px;">API keys allow partners to access your horoscope data programmatically.</p>
      </div>

      <div class="studio-form-actions">
        <button class="studio-btn-primary" @click="requestKey" :disabled="requesting">
          {{ requesting ? 'Creating...' : '✦ Create New API Key' }}
        </button>
        <span v-if="newKeyValue" class="new-key-notice">
          New key created. Copy it now - it won't be shown again.
        </span>
      </div>

      <div v-if="newKeyValue" class="new-key-display studio-card" style="margin-top: 16px;">
        <label style="display: block; font-size: 11px; color: rgba(195,169,255,0.5); text-transform: uppercase; margin-bottom: 4px;">Your New API Key</label>
        <code class="key-value">{{ newKeyValue }}</code>
        <button class="copy-key-btn" @click="copyKey">{{ keyCopied ? 'Copied!' : 'Copy' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { static_store } from '@/store'
import { parseAxiosError } from '@/utils/general-utils'

const loading = ref(true)
const keys = ref([])
const requesting = ref(false)
const newKeyValue = ref('')
const keyCopied = ref(false)

function authHeaders() {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Token ${token}` } : {}
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function copyKey() {
  navigator.clipboard.writeText(newKeyValue.value)
  keyCopied.value = true
  setTimeout(() => { keyCopied.value = false }, 2000)
}

async function requestKey() {
  requesting.value = true
  try {
    const res = await axios.post(`${static_store.apiUrl}/reader/create-api-key/`, {}, { headers: authHeaders() })
    if (res.data?.key) {
      newKeyValue.value = res.data.key
      keys.value.unshift({
        id: res.data.id,
        name: res.data.name || 'API Key',
        prefix: res.data.prefix || res.data.key.slice(0, 8),
        created: new Date().toISOString(),
        revoked: false,
      })
    }
  } catch (error) {
    console.error('Create key failed:', parseAxiosError(error))
  } finally {
    requesting.value = false
  }
}

onMounted(async () => {
  try {
    const res = await axios.get(`${static_store.apiUrl}/reader/my-api-keys/`, { headers: authHeaders() })
    keys.value = res.data?.keys || []
  } catch {
    // Endpoint may not exist yet — show empty state
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
@import '@/styles/studio-forms.css';

.keys-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px; }
.key-card { padding: 14px 16px; }
.key-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.key-name { font-weight: 600; font-size: 14px; color: #E8D8FF; }
.key-status { font-size: 10px; padding: 2px 8px; border-radius: 10px; font-weight: 600; }
.key-status.active { background: rgba(125,232,204,0.15); color: #7DE8CC; }
.key-status.revoked { background: rgba(229,115,115,0.15); color: #e57373; }
.key-prefix { font-family: monospace; font-size: 13px; color: rgba(195,169,255,0.5); margin-bottom: 4px; }
.key-meta { font-size: 11px; color: rgba(195,169,255,0.3); }

.new-key-notice { font-size: 12px; color: #C9A94E; font-style: italic; }
.new-key-display { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.key-value { flex: 1; font-family: monospace; font-size: 12px; color: #7DE8CC; background: rgba(255,255,255,0.04); padding: 8px; border-radius: 4px; word-break: break-all; }
.copy-key-btn { padding: 6px 14px; background: rgba(201,169,78,0.15); border: 1px solid rgba(201,169,78,0.3); border-radius: 6px; color: #C9A94E; font-size: 12px; cursor: pointer; transition: background 0.2s; }
.copy-key-btn:hover { background: rgba(201,169,78,0.25); }
</style>
